import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ArrowLeft, Building2, Pencil } from 'lucide-react';
import {
  FormattedDepartmentSummary,
  useGetDepartmentByIdQuery,
  useGetDepartmentSummaryQuery,
} from '../store/slices/departmentSlice';
import { useDispatch } from 'react-redux';
import { openModal } from '../store/slices/uiSlice';
import { useAuth } from '../contexts/AuthContext';
import { isManagerLike } from '../utils/roles';
import { PageError, PageLoading } from '../components/common/PageState';
import {
  CountBadge,
  EmptyState,
  ErrorState,
  IconTile,
  Meter,
  PageHeader,
  PersonCell,
  SearchField,
  SpecGrid,
  StatusPill,
  TableCard,
  TableEmptyRow,
  Toolbar,
  useDelayedFlag,
} from '../components/ui';
import type { SpecItem } from '../components/ui';
import { sbg, transition } from '../theme/sbgTokens';
import { formatCount, formatDays, formatNumber, formatPercent, formatTime, MISSING } from '../utils/format';
import ResponsiveSkeletonRows, { SkeletonColumn } from './departments-parts/ResponsiveSkeletonRows';
import { labelSlotWidth } from './departments-parts/labelSlot';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

type Member = FormattedDepartmentSummary['employeeSummaries'][number];
type Balance = { total: number; used: number; remaining: number };
type SortKey = 'name' | 'pto';

/*
 * People table columns. The name stays pinned while the figures scroll beside
 * it on narrow screens; sick time drops below 768px.
 */
const STICKY_CLASS = 'department-people-sticky';
const NAME_MIN_WIDTH = { xs: 150, lg: 180 };
const stickyHeadSx = { position: 'sticky', left: 0, zIndex: 3, bgcolor: sbg.bg.surfaceSubtle, minWidth: NAME_MIN_WIDTH } as const;
const stickyBodySx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: sbg.bg.surface,
  transition: transition('background-color'),
  minWidth: NAME_MIN_WIDTH,
  maxWidth: 280,
} as const;
const HIDE_BELOW_MD = { display: { xs: 'none', md: 'table-cell' } } as const;

const MEMBER_SKELETON_COLUMNS: SkeletonColumn[] = [
  { key: 'name', sx: stickyBodySx },
  { key: 'pto' },
  { key: 'sick', sx: HIDE_BELOW_MD },
  { key: 'wfh', align: 'right' },
  { key: 'status' },
];
const MEMBER_COLUMNS = MEMBER_SKELETON_COLUMNS.length;

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Hours with a unit: 48h · 12.5h · — */
const formatHours = (hours: number | null | undefined) => {
  if (!isNumber(hours)) return MISSING;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)}h`;
};

/** "48 of 120h" */
const hoursOf = (used: number, total: number) =>
  `${formatNumber(used, Number.isInteger(used) ? 0 : 1)} of ${formatHours(total)}`;

/** "3.5 of 12 days" */
const daysOf = (used: number, total: number) => {
  const rounded = Math.round(used * 10) / 10;
  return `${formatNumber(rounded, Number.isInteger(rounded) ? 0 : 1)} of ${formatDays(total)}`;
};

const balanceOf = (balance: Partial<Balance> | undefined): Balance => {
  const total = balance?.total ?? 0;
  const used = balance?.used ?? 0;
  return { total, used, remaining: balance?.remaining ?? total - used };
};

/** Status color only when the state is true (book §6.5): over allotment, or none left. */
const meterTone = (balance: Balance): 'brand' | 'warning' | 'danger' => {
  if (balance.total > 0 && balance.remaining < 0) return 'danger';
  if (balance.total > 0 && balance.remaining === 0) return 'warning';
  return 'brand';
};

const usageShare = (balance: Balance) => (balance.total > 0 ? balance.used / balance.total : null);

/** The figures beside a usage meter ("48 of 120h"), or null when nothing is allotted and no meter shows. */
const usageFigures = (balance: Balance) => (balance.total > 0 ? hoursOf(balance.used, balance.total) : null);

/** Fixed label slot for one usage column: as wide as its longest figures, the same on every row. */
const usageSlot = (members: Member[], pick: (member: Member) => Partial<Balance> | undefined) =>
  labelSlotWidth(
    members.map((member) => usageFigures(balanceOf(pick(member)))).filter((figures): figures is string => figures !== null),
  );

/**
 * The data for the id in the URL, never another one's. On a direct hop between
 * two departments RTK Query keeps returning the previous id's `data` while the
 * new id loads (or after it fails); `currentData` is only ever this id's.
 */
const forThisId = <T,>(query: { data?: T; currentData?: T; isFetching?: boolean; isError?: boolean }): T | undefined =>
  query.currentData ?? (query.isFetching || query.isError ? undefined : query.data);

/**
 * A one-line usage meter for a table cell. The figures sit in a fixed,
 * right-aligned slot (`slot`, the same for every row of the column), so every
 * track starts and ends at the same x.
 */
const UsageCell: React.FC<{ balance: Balance; label: string; slot: string }> = ({ balance, label, slot }) => {
  if (balance.total === 0) {
    return (
      <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
        {balance.used > 0 ? `${formatHours(balance.used)} · none allotted` : MISSING}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(48px, 1fr) auto', alignItems: 'center', columnGap: 1.25, minWidth: 0 }}>
      <Meter
        value={balance.used}
        max={balance.total}
        tone={meterTone(balance)}
        aria-label={`${label}: ${hoursOf(balance.used, balance.total)} used`}
      />
      <Typography
        variant="body2"
        component="span"
        sx={{ width: slot, textAlign: 'right', color: sbg.text.primary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {hoursOf(balance.used, balance.total)}
      </Typography>
    </Box>
  );
};

/** Spec grid placeholders at the real size, revealed after 300ms so fast loads don't flash. */
const SpecGridSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        columnGap: 4,
        rowGap: 3,
        visibility: visible ? 'visible' : 'hidden',
      }}
    >
      {[0, 1, 2, 3].map((n) => (
        <Box key={n} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1 }}>
          <Skeleton variant="rounded" width={72} height={12} />
          <Skeleton variant="rounded" width="50%" height={28} />
          <Skeleton variant="rounded" width="70%" height={14} />
        </Box>
      ))}
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const DepartmentDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { profile } = useAuth();
  const role = profile?.role;
  /* PUT /departments/:id is admin/HR only; managers can view but not edit (matches EmployeeDetail). */
  const canEdit = role === 'admin' || role === 'hr';
  /* /departments is admin/HR only, so managers go back to their dashboard. */
  const managerView = isManagerLike(role);
  const backPath = managerView ? '/dashboard' : '/departments';
  const backName = managerView ? 'Dashboard' : 'Departments';

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const departmentQuery = useGetDepartmentByIdQuery(id);
  const {
    isLoading: isLoadingDepartment,
    error: departmentError,
    refetch: refetchDepartment,
  } = departmentQuery;
  const department = forThisId(departmentQuery);

  const summaryQuery = useGetDepartmentSummaryQuery(id);
  const {
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary,
    fulfilledTimeStamp,
  } = summaryQuery;
  const summary = forThisId(summaryQuery);

  /*
   * Pending until this department's data has arrived or failed. Every figure on
   * the page (header count, spec grid, people table) waits for it, so nothing
   * shows a 0 counted from a summary that hasn't loaded.
   */
  const departmentPending = isLoadingDepartment || (department === undefined && !departmentError && Boolean(departmentQuery.isFetching));
  const summaryPending = isLoadingSummary || (summary === undefined && !summaryError);

  const handleEdit = () => {
    if (department) {
      dispatch(openModal({ type: 'department-form', data: { department, isEdit: true } }));
    }
  };

  const totals = summary?.departmentTotals;
  const members: Member[] = useMemo(() => summary?.employeeSummaries ?? [], [summary]);
  const headcount = totals?.employeeCount || 0;

  /* One label slot per usage column, from everyone in the department, so it holds still while searching. */
  const ptoSlot = useMemo(() => usageSlot(members, (member) => member.pto), [members]);
  const sickSlot = useMemo(() => usageSlot(members, (member) => member.sickTime), [members]);

  const visibleMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const direction = sortDirection === 'asc' ? 1 : -1;
    return members
      .filter((member) => !query || (member.name ?? '').toLowerCase().includes(query))
      .sort((a, b) => {
        if (sortKey === 'pto') {
          const diff = (usageShare(balanceOf(a.pto)) ?? -1) - (usageShare(balanceOf(b.pto)) ?? -1);
          if (diff !== 0) return diff * direction;
          return (a.name ?? '').localeCompare(b.name ?? '');
        }
        return (a.name ?? '').localeCompare(b.name ?? '') * direction;
      });
  }, [members, search, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
    }
  };

  const backLink = (
    <Button
      component={RouterLink}
      to={backPath}
      variant="text"
      size="small"
      startIcon={<ArrowLeft size={16} />}
      aria-label={`Back to ${backName.toLowerCase()}`}
      sx={{ mb: 1.5, ml: -1, color: sbg.text.secondary, '&:hover': { color: sbg.text.primary } }}
    >
      {backName}
    </Button>
  );

  /* ---- Whole-page states: the department record itself ------------------ */

  if (departmentPending) {
    return (
      <Box>
        {backLink}
        <PageLoading label="Loading department…" variant="table" />
      </Box>
    );
  }

  if (departmentError || !department) {
    const notFound = (departmentError as { status?: number } | undefined)?.status === 404 || (!departmentError && !department);
    return (
      <Box>
        {backLink}
        {notFound ? (
          <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4, bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card }}>
            <EmptyState
              icon={<Building2 />}
              title="This department doesn’t exist"
              body="It may have been deleted, or the link is out of date."
              action={
                <Button component={RouterLink} to={backPath} variant="outlined">
                  {managerView ? 'Go to dashboard' : 'Go to departments'}
                </Button>
              }
            />
          </Box>
        ) : (
          <PageError
            title="Couldn’t load this department"
            description="The server didn’t respond. Try again in a minute."
            onRetry={() => {
              refetchDepartment();
              refetchSummary();
            }}
          />
        )}
      </Box>
    );
  }

  /* ---- Figures ----------------------------------------------------------- */

  const manager = department.manager?.trim();
  const hybridCount = members.filter((member) => (member.wfh?.total || 0) > 0).length;
  const pto = balanceOf(totals?.pto);
  const sick = balanceOf(totals?.sickTime);
  const wfh = balanceOf(totals?.wfh);
  const summaryReady = !summaryPending && !summaryError;

  const shareCaption = (balance: Balance) => {
    const share = usageShare(balance);
    return share === null ? 'Nothing allotted' : `of ${formatHours(balance.total)} · ${formatPercent(share * 100)} used`;
  };

  const specItems: SpecItem[] = [
    {
      label: 'People',
      value: formatNumber(headcount),
      caption: headcount > 0 ? `${formatNumber(hybridCount)} hybrid · ${formatNumber(headcount - hybridCount)} in office` : 'No one assigned yet',
    },
    {
      label: 'PTO used',
      value: pto.total > 0 ? formatHours(pto.used) : MISSING,
      caption: shareCaption(pto),
    },
    {
      label: 'Sick time used',
      value: sick.total > 0 ? formatHours(sick.used) : MISSING,
      caption: shareCaption(sick),
    },
    {
      label: 'WFH used',
      value: wfh.total > 0 ? formatDays(wfh.used) : MISSING,
      caption: wfh.total > 0 ? `of ${formatDays(wfh.total)} across ${formatCount(hybridCount, 'hybrid person', 'hybrid people')}` : 'No one here is hybrid-eligible',
    },
  ];

  const sortHead = (key: SortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDirection : key === 'name' ? 'asc' : 'desc'}
      onClick={() => handleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const renderMembers = () => {
    if (summaryPending) return <ResponsiveSkeletonRows columns={MEMBER_SKELETON_COLUMNS} rows={6} />;
    if (summaryError) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={MEMBER_COLUMNS} sx={{ borderBottom: 0 }}>
            <ErrorState
              compact
              title="Couldn’t load the people in this department"
              body="Time-off balances didn’t load. Try again in a minute."
              onRetry={() => { refetchSummary(); }}
            />
          </TableCell>
        </TableRow>
      );
    }
    if (members.length === 0) {
      return (
        <TableEmptyRow
          colSpan={MEMBER_COLUMNS}
          title="No one in this department yet"
          body={`People appear here once ${department.name} is set as the department on their employee record.`}
        />
      );
    }
    if (visibleMembers.length === 0) {
      return (
        <TableEmptyRow
          colSpan={MEMBER_COLUMNS}
          title="No people match this search"
          body={`No one in ${department.name} is named “${search.trim()}”.`}
          action={<Button variant="outlined" onClick={() => setSearch('')}>Clear search</Button>}
        />
      );
    }

    return visibleMembers.map((member) => {
      const memberPto = balanceOf(member.pto);
      const memberSick = balanceOf(member.sickTime);
      const memberWfh = balanceOf(member.wfh);
      const isHybrid = memberWfh.total > 0;
      const flags: { tone: 'danger' | 'warning'; label: string }[] = [];
      if (memberPto.total > 0 && memberPto.remaining < 0) flags.push({ tone: 'danger', label: `Over PTO by ${formatHours(-memberPto.remaining)}` });
      else if (memberPto.total > 0 && memberPto.remaining === 0) flags.push({ tone: 'warning', label: 'No PTO left' });
      if (memberSick.total > 0 && memberSick.remaining < 0) flags.push({ tone: 'danger', label: `Over sick time by ${formatHours(-memberSick.remaining)}` });

      return (
        <TableRow key={member.id} sx={{ [`&:hover > .${STICKY_CLASS}`]: { bgcolor: sbg.bg.hover } }}>
          <TableCell className={STICKY_CLASS} sx={stickyBodySx}>
            <PersonCell
              size={24}
              avatarName={member.name}
              name={
                <Link
                  component={RouterLink}
                  to={`/employees/${member.id}`}
                  underline="hover"
                  sx={{ color: sbg.text.primary, '&:hover': { color: sbg.text.link } }}
                >
                  {member.name}
                </Link>
              }
            />
          </TableCell>
          <TableCell sx={{ minWidth: 160 }}>
            <UsageCell balance={memberPto} label={`${member.name} PTO`} slot={ptoSlot} />
          </TableCell>
          <TableCell sx={{ ...HIDE_BELOW_MD, minWidth: 160 }}>
            <UsageCell balance={memberSick} label={`${member.name} sick time`} slot={sickSlot} />
          </TableCell>
          {/* WFH is only allotted to hybrid people; everyone else reads as in office. */}
          <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: isHybrid ? sbg.text.primary : sbg.text.secondary }}>
            {isHybrid ? daysOf(memberWfh.used, memberWfh.total) : 'In office'}
          </TableCell>
          <TableCell>
            {flags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {flags.map((flag) => (
                  <StatusPill key={flag.label} tone={flag.tone}>{flag.label}</StatusPill>
                ))}
              </Box>
            )}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Box>
      {backLink}

      <PageHeader
        overline="Department"
        title={department.name}
        leading={<IconTile size={40}><Building2 /></IconTile>}
        meta={[
          manager ? `Managed by ${manager}` : 'No manager assigned',
          summaryReady ? formatCount(headcount, 'person', 'people') : null,
          department.isSalesTeam ? 'Sales team' : null,
          fulfilledTimeStamp ? `Updated ${formatTime(fulfilledTimeStamp)}` : null,
        ]}
        actions={
          canEdit ? (
            <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={handleEdit}>
              Edit department
            </Button>
          ) : undefined
        }
      />

      {!summaryError && (
        <Box component="section" aria-label="This year at a glance" sx={{ mb: 3 }}>
          {summaryPending ? (
            <SpecGridSkeleton />
          ) : (
            <SpecGrid items={specItems} />
          )}
        </Box>
      )}

      <TableCard
        aria-label={`People in ${department.name}`}
        toolbar={
          <Toolbar
            end={
              members.length > 0 ? (
                <SearchField value={search} onChange={setSearch} label="Search people" placeholder="Search by name" width={240} />
              ) : undefined
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                People
              </Typography>
              {summaryReady && <CountBadge count={members.length} label={formatCount(members.length, 'person', 'people')} />}
            </Box>
            <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, display: { xs: 'none', md: 'inline' } }}>
              Balances for the current calendar year
            </Typography>
          </Toolbar>
        }
      >
        <Table aria-label={`People in ${department.name}`}>
          <TableHead>
            <TableRow>
              <TableCell sx={stickyHeadSx} sortDirection={sortKey === 'name' ? sortDirection : false}>
                {sortHead('name', 'Name')}
              </TableCell>
              <TableCell sortDirection={sortKey === 'pto' ? sortDirection : false}>{sortHead('pto', 'PTO used')}</TableCell>
              <TableCell sx={HIDE_BELOW_MD}>Sick time used</TableCell>
              <TableCell align="right">WFH used</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderMembers()}</TableBody>
        </Table>
      </TableCard>
    </Box>
  );
};

export default DepartmentDetail;
